'use client';

import { useState } from 'react';

export default function EmiCalculator() {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTenure, setLoanTenure] = useState('');
  const [tenureType, setTenureType] = useState<'years' | 'months'>('years');
  const [emi, setEmi] = useState<number | null>(null);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);
  const [totalPayment, setTotalPayment] = useState<number | null>(null);

  const calculateEMI = () => {
    if (!loanAmount || !interestRate || !loanTenure) return;

    const principal = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate) / 100;
    const tenure = parseFloat(loanTenure);

    if (principal <= 0 || annualRate < 0 || tenure <= 0) return;

    // Convert tenure to months
    const tenureInMonths = tenureType === 'years' ? tenure * 12 : tenure;
    const monthlyRate = annualRate / 12;

    // EMI calculation formula: P * r * (1 + r)^n / ((1 + r)^n - 1)
    const emiValue = principal * monthlyRate * Math.pow(1 + monthlyRate, tenureInMonths) /
      (Math.pow(1 + monthlyRate, tenureInMonths) - 1);

    const totalPaymentValue = emiValue * tenureInMonths;
    const totalInterestValue = totalPaymentValue - principal;

    setEmi(Math.round(emiValue));
    setTotalInterest(Math.round(totalInterestValue));
    setTotalPayment(Math.round(totalPaymentValue));
  };

  const resetCalculator = () => {
    setLoanAmount('');
    setInterestRate('');
    setLoanTenure('');
    setTenureType('years');
    setEmi(null);
    setTotalInterest(null);
    setTotalPayment(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          EMI Calculator
        </h2>

        <div className="space-y-6">
          <div>
            <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700 mb-2">
              Loan Amount (₹)
            </label>
            <input
              type="number"
              id="loanAmount"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              placeholder="Enter loan amount"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              min="1"
              step="1000"
            />
          </div>

          <div>
            <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 mb-2">
              Interest Rate (% per annum)
            </label>
            <input
              type="number"
              id="interestRate"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="Enter interest rate"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              min="0"
              step="0.1"
            />
          </div>

          <div>
            <label htmlFor="loanTenure" className="block text-sm font-medium text-gray-700 mb-2">
              Loan Tenure
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                id="loanTenure"
                value={loanTenure}
                onChange={(e) => setLoanTenure(e.target.value)}
                placeholder="Enter tenure"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                min="1"
              />
              <select
                value={tenureType}
                onChange={(e) => setTenureType(e.target.value as 'years' | 'months')}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              >
                <option value="years">Years</option>
                <option value="months">Months</option>
              </select>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={calculateEMI}
              disabled={!loanAmount || !interestRate || !loanTenure}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Calculate EMI
            </button>

            <button
              onClick={resetCalculator}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          </div>

          {emi && (
            <div className="mt-6 p-6 bg-gray-50 rounded-lg">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly EMI</h3>
                <p className="text-3xl font-bold text-blue-600">{formatCurrency(emi)}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Principal Amount</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(parseFloat(loanAmount))}</p>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Total Interest</p>
                  <p className="text-lg font-semibold text-red-600">{formatCurrency(totalInterest!)}</p>
                </div>

                <div className="bg-white p-4 rounded-lg sm:col-span-2">
                  <p className="text-sm text-gray-600 mb-1">Total Payment</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(totalPayment!)}</p>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Loan Summary:</strong></p>
                <ul className="mt-2 space-y-1">
                  <li>• Monthly EMI: {formatCurrency(emi)}</li>
                  <li>• Total Interest: {formatCurrency(totalInterest!)}</li>
                  <li>• Total Payment: {formatCurrency(totalPayment!)}</li>
                  <li>• Interest Percentage: {((totalInterest! / parseFloat(loanAmount)) * 100).toFixed(1)}%</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}